import { z } from 'zod';
import { EnrichedModule } from './EnrichedModule.js';
import { useClassModuleRegistry } from '../helpers/ModuleRegistry.js';
import { enrichedModuleSchemaSchema, } from '../interfaces/enrichedModuleSchema.js';
/**
 * Holds the entire specification of an application along with a module registry
 * for your convenience.
 */
export class EnrichedApp {
    modules = [];
    moduleRegistry = useClassModuleRegistry();
    /**
     * Constructs an EnrichedApp instance from an array of module POJOs or EnrichedModule class instances.
     *
     * This constructor accepts a list of modules, which may be either plain objects or EnrichedModule instances.
     * It sorts the modules by their `order` property to ensure they are in the correct sequence, and then adds
     * each module to the app. If any module's `order` does not match its position in the sorted array, an error
     * is thrown to enforce strict ordering and maintain data integrity.
     *
     * @param params - The constructor parameters, including an array of modules.
     * @throws ModulesOutOfOrderError if the modules are not ordered correctly by their `order` property.
     */
    constructor(params) {
        params.modules
            // Sort modules by their 'order' property to ensure correct sequence
            .toSorted((a, b) => a.order - b.order)
            .forEach((module, index) => {
            // Enforce strict ordering: throw if a module's order does not match its index
            if (module.order !== index)
                throw new ModulesOutOfOrderError('Modules should be ordered correctly', params.modules);
            return this.addModule(module instanceof EnrichedModule ? module : new EnrichedModule(module, this));
        });
    }
    /**
     * Instantiates an EnrichedApp from a JSON string.
     *
     * This method parses the provided JSON string, which should represent an array of enriched module objects,
     * and constructs a new EnrichedApp instance using those modules. Note that this method does not perform
     * schema validation; it assumes the JSON is well-formed and matches the expected structure.
     *
     * @param json - A JSON string representing an array of enriched module objects.
     * @returns An EnrichedApp instance populated with the parsed modules.
     * @throws If the JSON is malformed or cannot be parsed.
     * @throws ModulesOutOfOrderError if the modules are not ordered correctly by their `order` property.
     *
     * @example
     * ```typescript
     * const json = '[{"name":"User","order":0,"model":{...}},{"name":"Post","order":1,"model":{...}}]'
     * const app = EnrichedApp.fromJSON(json)
     * ```
     */
    static fromJSON(json) {
        return new EnrichedApp({
            modules: JSON.parse(json),
        });
    }
    /**
     * Safely creates an EnrichedApp instance from a JSON string.
     *
     * This method parses the provided JSON string and validates its structure
     * against the enriched module schema. If the JSON is invalid or does not
     * conform to the expected schema, an error is thrown.
     *
     * @param json - A JSON string representing an array of enriched modules.
     * @returns An EnrichedApp instance populated with validated modules.
     * @throws If the JSON is malformed or does not match the schema.
     * @throws ModulesOutOfOrderError if the modules are not ordered correctly by their `order` property.
     *
     * @example
     * ```typescript
     * const json = '[{"name":"User","order":0,"model":{...}},{"name":"Post","order":1,"model":{...}}]'
     * const app = EnrichedApp.fromJSONSafe(json)
     * ```
     */
    static fromJSONSafe(json) {
        return new EnrichedApp({
            modules: z.array(enrichedModuleSchemaSchema).parse(json),
        });
    }
    /**
     * Converts the EnrichedApp instance into an array of plain JavaScript objects (POJOs)
     * representing each module. This is useful for serialization, transport, or storage,
     * as it strips away class methods and returns only the data structure.
     *
     * @returns {EnrichedModuleSchema[]} An array of plain objects representing the modules.
     *
     * @example
     * ```typescript
     * const app = new EnrichedApp({...})
     * const pojo = app.toPOJO()
     * // Returns: [{ name: 'User', order: 0, model: {...} }, { name: 'Post', order: 1, model: {...} }]
     * ```
     */
    toPOJO() {
        return this.modules.map((module) => module.toPOJO());
    }
    /**
     * Serializes the EnrichedApp instance to a JSON string for transport or storage.
     * This method converts all modules to plain objects before stringifying.
     *
     * @returns {string} A JSON string representation of the EnrichedApp.
     *
     * @example
     * ```typescript
     * const app = new EnrichedApp({...})
     * const json = app.toJSON()
     * // Returns: '[{"name":"User","order":0,"model":{...}},{"name":"Post","order":1,"model":{...}}]'
     * ```
     */
    toJSON() {
        return JSON.stringify(this.toPOJO());
    }
    /**
     * Iterates through all modules and their relations in the EnrichedApp,
     * invoking the service container's generateForeignKeysAction for each relation.
     * This method mutates the modules in place by generating and attaching the necessary foreign keys.
     *
     * @param serviceContainer - The service container providing the generateForeignKeysAction.
     * @returns Promise<void>
     *
     * @example
     * ```typescript
     * const app = new EnrichedApp({...})
     * await app.generateForeignKeys(serviceContainer)
     * // All relations now have foreign key columns generated
     * ```
     */
    async generateForeignKeys(serviceContainer) {
        for (const module of this.modules) {
            for (const relationName of Object.keys(module.model.relations)) {
                const relation = module.model.relations[relationName];
                await serviceContainer.generateForeignKeysAction.run(module, this.modules, relation);
            }
        }
    }
    /**
     * Adds a new module to the app at the specified position, shifting other modules as needed.
     *
     * - Throws a DuplicateModuleError if a module with the same name already exists in the registry.
     * - Throws a ModuleOrderGapError if the position would create a gap in module order.
     * - The new module is inserted at the given position in the modules array (default is at the end).
     * - The order property of the new and subsequent modules is updated accordingly.
     * - The module is registered in the moduleRegistry and its moduleRegistry property is set.
     *
     * @param module - The module instance or constructor params to add.
     * @param position - The index at which to insert the module (default: end of modules array).
     * @returns The EnrichedApp instance (for chaining).
     * @throws DuplicateModuleError if a module with the same name already exists in the registry.
     * @throws ModuleOrderGapError if the position would create a gap in module order.
     *
     * @example
     * ```typescript
     * const app = new EnrichedApp({...})
     * const module = new EnrichedModule({...})
     * app.addModule(module, 1) // Inserts at position 1
     * ```
     */
    addModule(module, position = this.modules.length) {
        if (this.moduleRegistry.has(module.name)) {
            throw new DuplicateModuleError('Module with the same name already added to registry', module);
        }
        // Validate that position doesn't create a gap
        if (position > this.modules.length) {
            throw new ModuleOrderGapError(`Cannot add module at position ${position}. This would create a gap in module order. Maximum allowed position is ${this.modules.length}.`, position, this.modules.length);
        }
        this.setModule(module, position);
        return this;
    }
    /**
     * Inserts or replaces a module at the specified position in the app.
     *
     * - If a module with the same name already exists, it will be overwritten in the registry.
     * - Throws a ModuleOrderGapError if the position would create a gap in module order.
     * - The module is inserted at the given position in the modules array.
     * - The order property of the inserted module and all subsequent modules is updated accordingly.
     * - The module is registered in the moduleRegistry and its moduleRegistry property is set.
     *
     * WARNING: This will overwrite any existing module with the same name in the registry.
     *
     * @param module - The module instance or constructor params to insert or replace.
     * @param position - The index at which to insert the module.
     * @throws ModuleOrderGapError if the position would create a gap in module order.
     *
     * @example
     * ```typescript
     * const app = new EnrichedApp({...})
     * const module = new EnrichedModule({...})
     * app.setModule(module, 1) // Inserts/replaces at position 1
     * ```
     */
    setModule(module, position) {
        // Validate that position doesn't create a gap
        if (position > this.modules.length) {
            throw new ModuleOrderGapError(`Cannot set module at position ${position}. This would create a gap in module order. Maximum allowed position is ${this.modules.length}.`, position, this.modules.length);
        }
        const moduleInstance = module instanceof EnrichedModule ? module : new EnrichedModule(module, this);
        moduleInstance.order = position;
        moduleInstance.parentApp = this;
        // Insert module at position
        this.modules.splice(position, 0, moduleInstance);
        // Update position of following modules
        this.modules.slice(position + 1).map((m) => m.order++);
        this.moduleRegistry.set(moduleInstance.name, moduleInstance);
    }
    /**
     * Removes a module from the app by its name.
     *
     * - Throws if the module does not exist in the registry.
     * - Removes all relations associated with the module.
     * - Deletes the module from the registry and modules array.
     * - Updates the order of remaining modules after removal.
     *
     * @param name - The name of the module to remove.
     * @throws ModuleDoesntExistInRegistryError if the module does not exist in the registry.
     *
     * @example
     * ```typescript
     * const app = new EnrichedApp({...})
     * app.removeModule('User') // Removes the User module and all its relations
     * ```
     */
    removeModule(name) {
        if (!this.moduleRegistry.has(name)) {
            throw new ModuleDoesntExistInRegistryError("Module doesn't exist in registry", name, this.moduleRegistry);
        }
        const module = this.moduleRegistry.get(name);
        for (const relation of Object.values(module.model.relations)) {
            module.removeRelation(relation.name);
        }
        this.moduleRegistry.delete(name);
        const moduleIndex = this.modules.findIndex((module) => module.name === name);
        this.modules.splice(moduleIndex, 1);
        this.modules.slice(moduleIndex).map((m) => m.order--);
    }
    /**
     * Moves a module to a new position in the modules array and updates order properties.
     *
     * This method reorders the modules array by moving the specified module to the given position.
     * It updates the `order` property of all affected modules to ensure consistency.
     * After the move, the modules array is sorted by the updated order.
     *
     * @param name - The name of the module to move.
     * @param position - The target index to move the module to.
     * @throws ModuleDoesntExistInRegistryError if no module with the specified name exists.
     * @throws ModuleOrderGapError if the position would create a gap in module order.
     *
     * @example
     * ```typescript
     * const app = new EnrichedApp({...})
     * app.moveModule('User', 2) // Moves User module to position 2
     * ```
     */
    moveModule(name, position) {
        if (!this.moduleRegistry.has(name)) {
            throw new ModuleDoesntExistInRegistryError("Module doesn't exist in registry", name, this.moduleRegistry);
        }
        // Validate that position doesn't create a gap
        if (position >= this.modules.length) {
            throw new ModuleOrderGapError(`Cannot move module to position ${position}. This would create a gap in module order. Maximum allowed position is ${this.modules.length - 1}.`, position, this.modules.length);
        }
        const module = this.moduleRegistry.get(name);
        // The amount of positions we need to move the module
        const moveAmount = Math.abs(module.order - position);
        if (moveAmount === 0)
            return;
        // The position of the module we start moving from
        const startModuleOrder = module.order;
        // The direction we are moving the module
        const direction = module.order < position ? 'down' : 'up';
        // Loop over all the modules we need to move and move them one down or one up
        for (let i = 1; i <= moveAmount; i++) {
            if (direction === 'down') {
                // Moving down: shift modules between current and target position up
                this.modules[startModuleOrder + i].order -= 1;
            }
            else {
                // Moving up: shift modules between target and current position down
                this.modules[startModuleOrder - i].order += 1;
            }
        }
        // Move the provided module to the new position
        module.order = position;
        // Sort the modules array to reflect the new order
        this.modules.sort((a, b) => a.order - b.order);
    }
    /**
     * Removes all columns of type 'foreignId' from every module in the app.
     *
     * Iterates through each module and deletes any column whose type is 'foreignId'
     * from the module's model columns. This operation mutates the modules in place,
     * ensuring that no foreign key columns remain in any module definition.
     *
     * @example
     * ```typescript
     * const app = new EnrichedApp({...})
     * app.removeForeignKeyColumns()
     * // All foreignId columns are removed from all modules
     * ```
     */
    removeForeignKeyColumns() {
        for (const module of this.modules) {
            for (const columnKey of Object.keys(module.model.columns)) {
                if (module.model.columns[columnKey].type === 'foreignId') {
                    delete module.model.columns[columnKey];
                }
            }
        }
    }
}
export class DuplicateModuleError extends Error {
    module;
    constructor(message, module) {
        super(message);
        this.module = module;
        this.name = 'DuplicateModuleError';
    }
}
export class ModulesOutOfOrderError extends Error {
    modules;
    constructor(message, modules) {
        super(message);
        this.modules = modules;
        this.name = 'ModulesOutOfOrderError';
    }
}
export class ModuleDoesntExistInRegistryError extends Error {
    name;
    registry;
    constructor(message, name, registry) {
        super(message);
        this.name = name;
        this.registry = registry;
        this.name = 'ModuleDoesntExistInRegistryError';
    }
}
export class ModuleOrderGapError extends Error {
    position;
    currentModuleCount;
    constructor(message, position, currentModuleCount) {
        super(message);
        this.position = position;
        this.currentModuleCount = currentModuleCount;
        this.name = 'ModuleOrderGapError';
    }
}
